'use client';

import React from 'react';
import { Section, Container } from '@layout';
import { withOptionalId } from '@lib/withOptionalId';
import {LogicPills, Heading} from '@ui';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

type Result = { id: number | string; title: string; tags: string[] };

const TAG_MAP_ES: Record<string, string> = {
  'Version: I': 'Versión: I',
  'Version: II': 'Versión: II',
  'Status: Deployed': 'Estado: Desplegado',
  'Status: Sprint Planning': 'Estado: Planificacion de sprints',
  'Phase: UI Design': 'Fase: Diseño de UI',
  'Phase: UI Development': 'Fase: Desarrollo de UI',
};

const SUGGESTIONS_EN = ['Version: II', 'Status: Deployed', 'Phase: UI Design'];

const makeDemoResults = (n = 12, isSpanish = false): Result[] =>
  Array.from({ length: n }).map((_, i) => ({
    id: i + 1,
    title: isSpanish ? `Resultado ${i + 1}` : `Result ${i + 1}`,
    tags: [
      i % 2 === 0 ? 'Version: II' : 'Version: I',
      i % 3 === 0 ? 'Status: Deployed' : 'Status: Sprint Planning',
      i % 4 === 0 ? 'Phase: UI Design' : 'Phase: UI Development',
    ].map((tag) => (isSpanish ? TAG_MAP_ES[tag] ?? tag : tag)),
  }));

type Props = {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
};

export default function LogicPillsDemo({
  isSpanish = false,
  sectionID = 'logic-pills-demo',
  classes = '',
  containerId,
  containerClasses,
}: Props): React.ReactElement {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const suggestions = isSpanish ? SUGGESTIONS_EN.map((s) => TAG_MAP_ES[s] ?? s) : SUGGESTIONS_EN;
  const allResults = makeDemoResults(18, isSpanish);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as="h3">{t('Logic Pills (Selected Filters)', 'Píldoras de lógica (Filtros seleccionados)')}</Heading>
        <p className='sub'>
          {t(
            'Removable filter chips with AND/OR logic and quick “clear all”.',
            'Chips de filtro removibles con lógica AND/OR y “borrar todo” rápido.'
          )}
        </p>
        <LogicPills data={allResults} suggestions={suggestions} />
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="logic-pill-row" role="list">
  <button class="lp-pill chip btn-sm" type="button">Status</button>
  <span class="lp-connector">AND</span>
  <button class="lp-pill chip btn-sm" type="button">Owner</button>
</div>`}
        />
      </Container>
    </Section>
  );
}
